import {FC, useCallback, useEffect, useState} from 'react'
import {Content} from '@prismicio/client'
import {PrismicRichText, SliceComponentProps} from '@prismicio/react'
import {isFilled} from '@prismicio/helpers'
import {Context} from 'src/common/types'
import {PrismicNextImage} from '@prismicio/next'
import {AnimatePresence, motion} from 'framer-motion'
import {Icon} from 'src/common/Icon'
import {EVENTS, track, TrackingProps} from 'src/common/helpers'
import cn from 'classnames'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import {useWindowSize} from 'react-use'
import {BREAKPOINTS} from 'src/common/constants'

const variants = {
  initial: {opacity: 0.1},
  animate: {opacity: 1, transition: {delay: 0.3, duration: 0.7}, ease: 'easeInOut'},
  exit: {opacity: 0, transition: {duration: 0.5}},
}

const contentVariants = {
  initial: {opacity: 0},
  animate: {opacity: 1, transition: {delay: 0.4, duration: 0.7}, ease: 'easeInOut'},
  exit: {opacity: 0, transition: {duration: 0.5}},
}

/**
 * Props for `TestimonialsSection`.
 */
export type TestimonialsSectionProps = SliceComponentProps<Content.TestimonialsSectionSlice> & {context: Context}

/**
 * Component for "TestimonialsSection" Slices.
 */
const TestimonialsSection: FC<TestimonialsSectionProps> = ({slice, index: componentIndex, context}) => {
  const [activeTab, setActiveTab] = useState(0)
  const {testimonials, ratings_section} = slice.primary
  const {linkResolver} = useLinkResolver()
  const isMobile = useWindowSize().width < BREAKPOINTS.TABLET

  const handleClick = useCallback(
    (index: number, trackingData: TrackingProps) => {
      track(trackingData)
      setActiveTab(index)
    },
    [setActiveTab],
  )

  // Preload all images when the component mounts
  useEffect(() => {
    if (!isFilled.group(testimonials)) {
      return
    }

    testimonials.forEach((testimonial) => {
      if (testimonial.image.url) {
        const img = new Image()
        img.src = testimonial.image.url
      }
    })
  }, [testimonials])

  if (!isFilled.group(testimonials)) {
    return null
  }

  return (
    <div className="flex flex-col bg-moss-white pb-0 pt-10 lg:pt-20">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-5 md:gap-12 md:px-10 lg:px-5 xl:px-0">
        <div className="flex flex-col gap-3 md:gap-6">
          <PrismicRichText
            field={slice.primary.tag}
            components={{
              paragraph: ({children}) => (
                <p className="text-tg-body-s md:text-button-s font-semibold uppercase text-moss-grey-400">{children}</p>
              ),
              strong: ({children}) => <strong className="pr-3 text-moss-blue">{children}</strong>,
            }}
          />
          <PrismicRichText
            components={{
              heading1: ({children}) => (
                <h1 className="md:text-tg-h2 text-[32px] font-normal leading-9 tracking-[-.32px] text-moss-grey-400 md:leading-[normal]">
                  {children}
                </h1>
              ),
              strong: ({children}) => (
                <>
                  <span className="md:text-tg-h2 text-[32px] leading-9 tracking-[-.32px] text-black md:leading-[normal]">
                    {children}
                  </span>
                  <br className="max-lg:hidden" />
                </>
              ),
            }}
            field={slice.primary.title}
          />
        </div>
        <div className="flex gap-2 max-md:pb-10">
          {testimonials.map((testimonial, index) => (
            <button
              key={index}
              onClick={() =>
                handleClick(index, {
                  event: EVENTS.CLICK,
                  location: `component-${componentIndex}-testimonials`,
                  pageName: context.pageUid,
                  mktgId: `testimonial-tab-${index}`,
                  dataType: 'testimonial-tab',
                })
              }
              className={cn(
                'md:text-button-s h-8 rounded-[4px] px-3 py-[9px] text-[11px] font-semibold uppercase tracking-[.66px]',
                {
                  'bg-green-neon text-black': index === activeTab,
                  'bg-moss-grey-100 text-moss-grey-500 hover:bg-moss-grey-150': index !== activeTab,
                },
              )}
            >
              {testimonial.tab_title}
            </button>
          ))}
        </div>
      </div>
      <div className="relative isolate grid w-full grid-cols-[minmax(20px,_1fr)_repeat(12,_minmax(auto,_100px))_minmax(20px,_1fr)] grid-rows-[auto_40px_168px_168px_168px_40px] md:grid-rows-[64px,532px,40px,168px,80px] md:max-lg:grid-cols-[minmax(40px,_1fr)_repeat(12,_minmax(auto,_100px))_minmax(40px,_1fr)]">
        <Gradient color="255, 255, 255" gradientStart={20} height={40} location="top" className="max-md:hidden" />

        {/* First row */}
        <div className="col-span-1 rounded-br-[4px] border-b border-r border-moss-grey-150 max-md:row-start-2" />
        <div className="col-span-12 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:relative max-md:row-start-2">
          <Gradient
            color="255, 255, 255"
            gradientStart={20}
            height={40}
            location="top"
            className="left-[-1px] w-[calc(100%+5px)] md:hidden"
          />
        </div>
        <div className="col-span-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-moss-grey-150 max-md:row-start-2" />

        {/* Image section */}
        <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:col-start-1 max-md:row-start-3" />
        <div className="col-span-full ml-[-1px] mt-[-1px] flex flex-col items-center justify-center gap-6 overflow-hidden rounded-[4px] border border-moss-grey-150 max-md:relative max-md:h-[558px] max-md:border-none max-md:px-5 md:col-span-12">
          <div className="relative flex h-full w-full flex-col p-10 max-md:overflow-hidden max-md:rounded-[8px]">
            <AnimatePresence mode="sync">
              <motion.img
                className="absolute inset-0 -z-10 h-full w-full overflow-hidden object-cover"
                key={activeTab}
                src={
                  isMobile && isFilled.image(testimonials[activeTab].mobile_image)
                    ? testimonials[activeTab].mobile_image.url
                    : testimonials[activeTab].image.url!
                }
                variants={variants}
                initial="initial"
                animate="animate"
                exit="exit"
              />
            </AnimatePresence>
            <AnimatePresence mode="sync">
              <motion.div
                className="absolute inset-0 flex h-full w-full flex-col justify-between p-6 md:p-10"
                key={activeTab}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <div className="flex flex-col gap-3 md:gap-8">
                  <OpenQuote />
                  <PrismicRichText
                    field={testimonials[activeTab].quote}
                    components={{
                      paragraph: ({children}) => (
                        <p className="text-[22px] leading-[24px] text-moss-white md:max-w-[648px] md:text-[32px] md:leading-[38px]">
                          {children}
                        </p>
                      ),
                    }}
                  />
                  <PrismicRichText
                    field={testimonials[activeTab].author}
                    components={{
                      paragraph: ({children}) => (
                        <p className="text-detail font-semibold uppercase text-moss-white max-md:pt-3 md:text-[13px] md:leading-4 md:tracking-[.52px]">
                          {children}
                        </p>
                      ),
                    }}
                  />
                </div>
                <div className="flex justify-end md:justify-between">
                  <PrismicNextImage
                    field={testimonials[activeTab].company_logo}
                    className="max-h-[30px] w-fit max-w-[195px] object-cover max-md:hidden"
                  />
                  {isFilled.keyText(testimonials[activeTab].link_label) && (
                    <Link
                      href={linkResolver(testimonials[activeTab].link_url)}
                      locale={testimonials[activeTab].link_url.lang || 'en-us'}
                      className="group relative flex h-[38px] items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
                      onClick={() =>
                        track({
                          event: EVENTS.CLICK,
                          mktgId: `testimonial-${activeTab}-button`,
                          location: `component-${componentIndex}-testimonials`,
                          pageName: context.pageUid,
                          dataType: 'customer-story-button',
                        })
                      }
                    >
                      <span className="text-button-s uppercase text-black">{testimonials[0].link_label}</span>
                      <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
                        <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
                      </div>
                    </Link>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 max-md:col-start-[14] max-md:row-start-4" />

        {/* Divider section */}
        <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:col-start-1 max-md:row-start-4" />
        <div className="col-span-12 ml-[-1px] mt-[-1px] rounded-[4px] border border-moss-grey-150 max-md:col-start-2 max-md:row-start-6" />
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 max-md:col-start-[14] max-md:row-start-3" />

        {/* Ratings section */}
        <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:row-start-5" />
        {ratings_section.map((rating, index) => (
          <div
            key={'ratings-' + index}
            // eslint-disable-next-line tailwindcss/no-custom-classname -- arbitrary value using the index
            className={`max-md:row-start- col-span-4 ml-[-1px] mt-[-1px] flex flex-col items-center justify-center gap-2 rounded-[4px] border border-moss-grey-150 p-4 max-md:col-start-2${
              index + 3
            } max-md:col-span-12`}
          >
            <PrismicRichText
              field={rating.content}
              components={{
                image: ({node}) => <img src={node.url} alt=" " className="h-8 w-8" />,
                heading5: ({children}) => (
                  <h5 className="text-tg-h3 text-center font-normal text-[#282828]">{children}</h5>
                ),
                em: ({children}) => (
                  <>
                    <span>{children}</span>
                    <br />
                  </>
                ),
                paragraph: ({children}) => (
                  <p className="text-detail text-center font-semibold uppercase leading-[normal] text-[#282828]/[60%]">
                    {children}
                  </p>
                ),
              }}
            />
          </div>
        ))}
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150" />

        {/* Last row */}
        <div className="col-span-1 mt-[-1px] rounded-tr-[4px] border-r border-t border-moss-grey-150 max-md:row-start-6" />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div className="col-span-4 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150" />
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-moss-grey-150 max-md:row-start-6" />

        <Gradient color="255, 255, 255" gradientStart={20} height={40} location="bottom" />
      </div>
    </div>
  )
}

export default TestimonialsSection

const Gradient = ({
  color,
  gradientStart,
  height,
  location,
  className,
}: {
  color: string
  gradientStart: number
  height: number
  location: 'top' | 'bottom'
  className?: string
}) => {
  return (
    <div
      className={`absolute w-full ${location === 'top' ? 'top-0' : 'bottom-0 mt-[-1px]'} ${className ? className : ''}`}
      style={{
        backgroundImage: `linear-gradient(to ${
          location === 'top' ? 'bottom' : 'top'
        }, rgb(${color}), rgb(${color}) ${gradientStart}%, transparent)`,
        height: `${height}px`,
      }}
    />
  )
}

const OpenQuote: FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="11" viewBox="0 0 21 11" fill="none">
    <path
      d="M9.406 0.000122404L6.94034 0.000122296C3.06474 0.000122127 0.5 2.67885 0.5 6.55445L0.5 11H4.83156L4.83156 4.84463H9.406L9.406 0.000122404Z"
      fill="white"
    />
    <path
      d="M20.8483 1.07773e-07L18.3826 0C14.507 -1.69408e-07 11.9423 2.67873 11.9423 6.55433V10.9999H16.2738V4.84451H20.8483V1.07773e-07Z"
      fill="white"
    />
  </svg>
)
